import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { chileRegions } from './cl-regions.js';

/**
 * Custom element that handles company invoice data collection.
 * A checkbox toggles a modal; data is saved as cart attributes.
 * The modal is teleported to document.body to avoid z-index issues
 * with the cart drawer's CSS transform stacking context.
 */
class CartInvoice extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {HTMLDialogElement | null} */
  #overlay = null;

  /** @type {((event: Event) => void) | null} */
  #cartUpdateHandler = null;

  /** @type {((event: Event) => void) | null} */
  #dialogOpenHandler = null;

  connectedCallback() {
    super.connectedCallback();
    this.#mountOverlay();
    this.#setupToggle();
    this.#restore();
    this.#setupCartUpdateListener();
    this.#setupDrawerOpenListener();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setupToggle();
    this.#restore();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#overlay?.remove();
    this.#overlay = null;

    if (this.#cartUpdateHandler) {
      document.removeEventListener('cart:updated', this.#cartUpdateHandler);
      this.#cartUpdateHandler = null;
    }

    if (this.#dialogOpenHandler) {
      document.removeEventListener('dialog:open', this.#dialogOpenHandler);
      this.#dialogOpenHandler = null;
    }

    this.#activeFetch?.abort();
    this.#activeFetch = null;
  }

  #mountOverlay() {
    if (this.#overlay) return;

    // Use <dialog> + showModal() so it enters the browser top layer,
    // sitting above the cart drawer's own <dialog> element.
    const overlay = document.createElement('dialog');
    overlay.className = 'cart-invoice__overlay';
    overlay.setAttribute('aria-labelledby', 'cart-invoice-modal-title');
    overlay.innerHTML = `
      <div class="cart-invoice__backdrop"></div>
      <div class="cart-invoice__modal" role="document">
        <div class="cart-invoice__modal-header">
          <h2 class="cart-invoice__modal-title" id="cart-invoice-modal-title">Datos de facturación</h2>
          <button class="cart-invoice__modal-close" type="button" aria-label="Cerrar">
            <span aria-hidden="true">&#x2715;</span>
          </button>
        </div>
        <div class="cart-invoice__modal-body">
          <div class="cart-invoice__field">
            <label class="cart-invoice__label" for="billing_company_name">
              Nombre o razón social <span class="cart-invoice__required" aria-hidden="true">*</span>
            </label>
            <input
              class="cart-invoice__input"
              type="text"
              id="billing_company_name"
              autocomplete="organization"
              placeholder="Empresa S.A."
            >
          </div>
          <div class="cart-invoice__field">
            <label class="cart-invoice__label" for="billing_giro">
              Giro <span class="cart-invoice__required" aria-hidden="true">*</span>
            </label>
            <input
              class="cart-invoice__input"
              type="text"
              id="billing_giro"
              placeholder="Comercio al por menor"
            >
          </div>
          <div class="cart-invoice__field">
            <label class="cart-invoice__label" for="billing_company_rut">
              RUT de empresa <span class="cart-invoice__required" aria-hidden="true">*</span>
            </label>
            <input
              class="cart-invoice__input"
              type="text"
              id="billing_company_rut"
              placeholder="76.000.000-0"
              maxlength="12"
            >
          </div>
          <div class="cart-invoice__field">
            <label class="cart-invoice__label" for="billing_address_1">
              Dirección tributaria <span class="cart-invoice__required" aria-hidden="true">*</span>
            </label>
            <input
              class="cart-invoice__input"
              type="text"
              id="billing_address_1"
              autocomplete="street-address"
              placeholder="Av. Ejemplo 1234"
            >
          </div>
          <div class="cart-invoice__field">
            <label class="cart-invoice__label" for="billing_address_2">
              N° casa / Dpto / Piso <span class="cart-invoice__required" aria-hidden="true">*</span>
            </label>
            <input
              class="cart-invoice__input"
              type="text"
              id="billing_address_2"
              placeholder="Dpto 4B"
            >
          </div>
          <div class="cart-invoice__field">
            <label class="cart-invoice__label" for="billing_region">
              Región <span class="cart-invoice__required" aria-hidden="true">*</span>
            </label>
            <select class="cart-invoice__select" id="billing_region">
              <option value="">Seleccione una región</option>
            </select>
          </div>
          <div class="cart-invoice__field">
            <label class="cart-invoice__label" for="billing_commune">
              Comuna <span class="cart-invoice__required" aria-hidden="true">*</span>
            </label>
            <select class="cart-invoice__select" id="billing_commune" disabled>
              <option value="">Seleccione región primero</option>
            </select>
          </div>
        </div>
        <div class="cart-invoice__modal-footer">
          <button class="cart-invoice__save-btn" type="button">Guardar</button>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);
    this.#overlay = overlay;

    // Intercept native Escape key: prevent default close and run our dismiss instead.
    overlay.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.#dismissWithClear();
    });

    this.#populateRegions();
    this.#attachOverlayListeners();
  }

  #setupToggle() {
    const checkbox = this.#getCheckbox();
    if (!checkbox) return;

    // Remove stale listener before re-attaching (updatedCallback may call this again)
    const prev = /** @type {any} */ (checkbox)._invoiceChangeHandler;
    if (prev) checkbox.removeEventListener('change', prev);

    /** @param {Event} event */
    const handler = (event) => {
      if (!(event.target instanceof HTMLInputElement)) return;
      if (event.target.checked) {
        this.#openModal();
      } else {
        this.#dismissWithClear();
      }
    };

    checkbox.addEventListener('change', handler);
    /** @type {any} */ (checkbox)._invoiceChangeHandler = handler;
  }

  #attachOverlayListeners() {
    if (!this.#overlay) return;

    this.#overlay.querySelector('.cart-invoice__backdrop')?.addEventListener('click', () => {
      this.#dismissWithClear();
    });

    this.#overlay.querySelector('.cart-invoice__modal-close')?.addEventListener('click', () => {
      this.#dismissWithClear();
    });

    this.#overlay.querySelector('.cart-invoice__save-btn')?.addEventListener('click', () => {
      this.#handleSave();
    });

    const regionSelect = this.#overlay.querySelector('#billing_region');
    regionSelect?.addEventListener('change', (event) => {
      if (event.target instanceof HTMLSelectElement) {
        this.#onRegionChange(event.target.value);
      }
    });

    const companyRut = this.#overlay.querySelector('#billing_company_rut');
    companyRut?.addEventListener('input', (event) => {
      if (event.target instanceof HTMLInputElement) {
        this.#formatRutInput(event.target);
      }
    });

    // Clear individual field error on input
    this.#overlay.querySelectorAll('.cart-invoice__input, .cart-invoice__select').forEach((el) => {
      el.addEventListener('input', (event) => {
        if (event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement) {
          this.#clearFieldError(event.target);
        }
      });
    });

    // Tab focus trap inside modal
    this.#overlay.addEventListener('keydown', (event) => {
      if (!(event instanceof KeyboardEvent)) return;
      if (event.key !== 'Tab' || !this.#isModalOpen()) return;
      this.#handleTabTrap(event);
    });
  }

  /** @param {KeyboardEvent} event */
  #handleTabTrap(event) {
    const modal = this.#overlay?.querySelector('.cart-invoice__modal');
    if (!modal) return;

    const nodes = modal.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
    );
    const focusable = Array.from(nodes).filter((el) => el instanceof HTMLElement);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey) {
      if (document.activeElement === first) {
        event.preventDefault();
        if (last instanceof HTMLElement) last.focus();
      }
    } else {
      if (document.activeElement === last) {
        event.preventDefault();
        if (first instanceof HTMLElement) first.focus();
      }
    }
  }

  #populateRegions() {
    const select = this.#overlay?.querySelector('#billing_region');
    if (!(select instanceof HTMLSelectElement)) return;

    select.innerHTML = '<option value="">Seleccione una región</option>';
    for (const region of chileRegions) {
      const option = document.createElement('option');
      option.value = region.name;
      option.textContent = region.name;
      select.appendChild(option);
    }
  }

  /** @param {string} regionName */
  #onRegionChange(regionName) {
    const communeSelect = this.#overlay?.querySelector('#billing_commune');
    if (!(communeSelect instanceof HTMLSelectElement)) return;

    communeSelect.innerHTML = '';

    if (!regionName) {
      communeSelect.disabled = true;
      const opt = document.createElement('option');
      opt.value = '';
      opt.textContent = 'Seleccione región primero';
      communeSelect.appendChild(opt);
      return;
    }

    const region = chileRegions.find((r) => r.name === regionName);
    if (!region) return;

    communeSelect.disabled = false;

    const defaultOpt = document.createElement('option');
    defaultOpt.value = '';
    defaultOpt.textContent = 'Seleccione una comuna';
    communeSelect.appendChild(defaultOpt);

    for (const commune of region.communes) {
      const opt = document.createElement('option');
      opt.value = commune;
      opt.textContent = commune;
      communeSelect.appendChild(opt);
    }
  }

  #openModal() {
    if (!this.#overlay) return;

    // Repopulate if mount-time population was missed or select was reset
    const regionSelect = this.#overlay.querySelector('#billing_region');
    if (regionSelect instanceof HTMLSelectElement && regionSelect.options.length <= 1) {
      this.#populateRegions();
    }

    const saved = this.#getFromStorage();
    if (saved) {
      this.#fillForm(saved);
    }

    this.#overlay.showModal();
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(() => {
      const firstInput = this.#overlay?.querySelector('.cart-invoice__input');
      if (firstInput instanceof HTMLElement) firstInput.focus();
    });
  }

  #closeModal() {
    if (!this.#overlay) return;
    this.#overlay.close();
    document.body.style.overflow = '';
  }

  // Uncheck checkbox + clear all data + close modal
  #dismissWithClear() {
    this.#closeModal();
    const checkbox = this.#getCheckbox();
    if (checkbox) checkbox.checked = false;
    this.#clearAllData();
  }

  async #handleSave() {
    if (!this.#validateForm()) return;

    const data = this.#collectFormData();
    this.#saveToStorage(data);
    await this.#saveToCart(data);
    this.#closeModal();
  }

  #validateForm() {
    if (!this.#overlay) return false;

    // Clear all previous errors
    this.#overlay.querySelectorAll('.cart-invoice__field-error').forEach((el) => el.remove());
    this.#overlay.querySelectorAll('.cart-invoice__input--error, .cart-invoice__select--error').forEach((el) => {
      el.classList.remove('cart-invoice__input--error', 'cart-invoice__select--error');
    });

    let valid = true;
    /** @type {string[]} */
    const fieldIds = [
      'billing_company_name',
      'billing_giro',
      'billing_company_rut',
      'billing_address_1',
      'billing_address_2',
      'billing_region',
      'billing_commune',
    ];

    for (const id of fieldIds) {
      const input = this.#overlay.querySelector(`#${id}`);
      if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement)) continue;

      if (!input.value.trim()) {
        this.#showFieldError(input, 'Este campo es obligatorio');
        valid = false;
        continue;
      }

      if (id === 'billing_company_rut') {
        if (!this.#rutUtils.validate(input.value.trim())) {
          this.#showFieldError(input, 'RUT inválido. Verifique el formato y el dígito verificador.');
          valid = false;
        }
      }
    }

    if (!valid) {
      const firstError = this.#overlay.querySelector('.cart-invoice__input--error, .cart-invoice__select--error');
      if (firstError instanceof HTMLElement) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstError.focus();
      }
    }

    return valid;
  }

  /**
   * @param {HTMLInputElement | HTMLSelectElement} input
   * @param {string} message
   */
  #showFieldError(input, message) {
    const errorClass = input instanceof HTMLSelectElement ? 'cart-invoice__select--error' : 'cart-invoice__input--error';
    input.classList.add(errorClass);

    const error = document.createElement('span');
    error.className = 'cart-invoice__field-error';
    error.textContent = message;
    error.setAttribute('role', 'alert');
    input.parentElement?.appendChild(error);
  }

  /** @param {HTMLInputElement | HTMLSelectElement} input */
  #clearFieldError(input) {
    input.classList.remove('cart-invoice__input--error', 'cart-invoice__select--error');
    const existing = input.parentElement?.querySelector('.cart-invoice__field-error');
    existing?.remove();
  }

  /**
   * @returns {{ company_name: string, giro: string, company_rut: string, address_1: string, address_2: string, region: string, commune: string }}
   */
  #collectFormData() {
    /** @param {string} id */
    const get = (id) => {
      const el = this.#overlay?.querySelector(`#${id}`);
      return el instanceof HTMLInputElement || el instanceof HTMLSelectElement ? el.value.trim() : '';
    };

    return {
      company_name: get('billing_company_name'),
      giro: get('billing_giro'),
      company_rut: get('billing_company_rut'),
      address_1: get('billing_address_1'),
      address_2: get('billing_address_2'),
      region: get('billing_region'),
      commune: get('billing_commune'),
    };
  }

  /**
   * @param {{ company_name: string, giro: string, company_rut: string, address_1: string, address_2: string, region: string, commune: string }} data
   */
  #fillForm(data) {
    if (!this.#overlay) return;

    /**
     * @param {string} id
     * @param {string} value
     */
    const set = (id, value) => {
      const el = this.#overlay?.querySelector(`#${id}`);
      if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement) el.value = value;
    };

    set('billing_company_name', data.company_name);
    set('billing_giro', data.giro);
    set('billing_company_rut', data.company_rut);
    set('billing_address_1', data.address_1);
    set('billing_address_2', data.address_2);

    if (data.region) {
      set('billing_region', data.region);
      this.#onRegionChange(data.region);
    }
    if (data.commune) {
      set('billing_commune', data.commune);
    }
  }

  /** @param {HTMLInputElement} input */
  #formatRutInput(input) {
    const value = input.value.trim();
    if (!value) return;

    const cleaned = this.#rutUtils.clean(value);
    if (!cleaned || cleaned.length < 2) return;

    const cursorAtEnd = input.selectionStart === input.value.length;
    const formatted = this.#rutUtils.format(cleaned, { dots: true });
    input.value = formatted;

    if (cursorAtEnd) {
      input.setSelectionRange(formatted.length, formatted.length);
    }
  }

  /**
   * @param {{ company_name: string, giro: string, company_rut: string, address_1: string, address_2: string, region: string, commune: string }} data
   */
  #saveToStorage(data) {
    try {
      localStorage.setItem('cart_invoice_data', JSON.stringify({ ...data, checked: true }));
    } catch (e) {
      this.#handleError(e, 'saveToStorage');
    }
  }

  /**
   * @returns {{ company_name: string, giro: string, company_rut: string, address_1: string, address_2: string, region: string, commune: string, checked: boolean } | null}
   */
  #getFromStorage() {
    try {
      const raw = localStorage.getItem('cart_invoice_data');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.checked === true ? parsed : null;
    } catch (e) {
      this.#handleError(e, 'getFromStorage');
      return null;
    }
  }

  #clearStorage() {
    try {
      localStorage.removeItem('cart_invoice_data');
    } catch (e) {
      this.#handleError(e, 'clearStorage');
    }
  }

  /**
   * @param {{ company_name: string, giro: string, company_rut: string, address_1: string, address_2: string, region: string, commune: string }} data
   */
  async #saveToCart(data) {
    if (this.#activeFetch) this.#activeFetch.abort();

    const controller = new AbortController();
    this.#activeFetch = controller;

    try {
      const body = {
        attributes: {
          billing_invoice: 'true',
          billing_company_name: data.company_name,
          billing_giro: data.giro,
          billing_company_rut: data.company_rut,
          billing_address_1: data.address_1,
          billing_address_2: data.address_2,
          billing_region: data.region,
          billing_commune: data.commune,
        },
      };

      const config = fetchConfig('json', { body: JSON.stringify(body) });
      await fetch(Theme.routes.cart_update_url, { ...config, signal: controller.signal });
    } catch (error) {
      this.#handleError(error, 'saveToCart');
    } finally {
      this.#activeFetch = null;
    }
  }

  async #clearFromCart() {
    if (this.#activeFetch) this.#activeFetch.abort();

    const controller = new AbortController();
    this.#activeFetch = controller;

    try {
      const body = {
        attributes: {
          billing_invoice: '',
          billing_company_name: '',
          billing_giro: '',
          billing_company_rut: '',
          billing_address_1: '',
          billing_address_2: '',
          billing_region: '',
          billing_commune: '',
        },
      };

      const config = fetchConfig('json', { body: JSON.stringify(body) });
      await fetch(Theme.routes.cart_update_url, { ...config, signal: controller.signal });
    } catch (error) {
      this.#handleError(error, 'clearFromCart');
    } finally {
      this.#activeFetch = null;
    }
  }

  #clearAllData() {
    this.#clearStorage();
    this.#clearFromCart();
  }

  #restore() {
    requestAnimationFrame(() => {
      const checkbox = this.#getCheckbox();
      if (!checkbox) return;

      const saved = this.#getFromStorage();
      checkbox.checked = Boolean(saved?.checked);
    });
  }

  #setupCartUpdateListener() {
    if (this.#cartUpdateHandler) {
      document.removeEventListener('cart:updated', this.#cartUpdateHandler);
    }

    this.#cartUpdateHandler = () => {
      setTimeout(() => {
        this.#setupToggle();
        this.#restore();
      }, 100);
    };

    document.addEventListener('cart:updated', this.#cartUpdateHandler);
  }

  #setupDrawerOpenListener() {
    if (this.#dialogOpenHandler) {
      document.removeEventListener('dialog:open', this.#dialogOpenHandler);
    }

    this.#dialogOpenHandler = (event) => {
      const drawer = event.target;
      if (drawer instanceof HTMLElement && drawer.classList.contains('cart-drawer')) {
        setTimeout(() => {
          this.#setupToggle();
          this.#restore();
        }, 100);
      }
    };

    document.addEventListener('dialog:open', this.#dialogOpenHandler);
  }

  #isModalOpen() {
    return this.#overlay instanceof HTMLDialogElement && this.#overlay.open;
  }

  /** @returns {HTMLInputElement | null} */
  #getCheckbox() {
    const el = this.querySelector('#invoice_required');
    return el instanceof HTMLInputElement ? el : null;
  }

  /**
   * @param {unknown} error
   * @param {string} context
   */
  #handleError(error, context) {
    if (
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.search.includes('debug=true'))
    ) {
      console.error(`[CartInvoice] ${context}:`, error);
    }
  }

  // ── Public methods called by cart-precheckout-guard ──────────────────────

  validateBeforeCheckout() {
    const checkbox = this.#getCheckbox();
    if (!checkbox?.checked) return true;

    const saved = this.#getFromStorage();

    if (!saved?.checked) {
      this.#openModal();
      return false;
    }

    const required = /** @type {(keyof typeof saved)[]} */ ([
      'company_name',
      'giro',
      'company_rut',
      'address_1',
      'address_2',
      'region',
      'commune',
    ]);
    const allFilled = required.every((key) => {
      const val = saved[key];
      return typeof val === 'string' && Boolean(val.trim());
    });

    if (!allFilled) {
      this.#openModal();
      return false;
    }

    return true;
  }

  async updateCartAttributesBeforeCheckout() {
    const checkbox = this.#getCheckbox();
    if (!checkbox?.checked) return;

    const saved = this.#getFromStorage();
    if (saved?.checked) {
      await this.#saveToCart(saved);
    }
  }

  // ── RUT utilities (same algorithm as cart-precheckout) ───────────────────

  #rutUtils = {
    /** @param {string} rut */
    clean: function (rut) {
      return typeof rut === 'string' ? rut.replace(/^0+|[^0-9kK]+/g, '').toUpperCase() : '';
    },

    /** @param {string} rut */
    validate: function (rut) {
      if (typeof rut !== 'string') return false;

      rut = this.clean(rut);
      if (!rut || rut.length < 2) return false;

      const numberPart = rut.slice(0, -1);
      const dv = rut.slice(-1);

      if (numberPart.length < 7) return false;
      if (!/^\d+$/.test(numberPart)) return false;
      if (!/^[0-9K]$/.test(dv)) return false;

      let t = parseInt(numberPart, 10);
      let m = 0;
      let s = 1;

      while (t > 0) {
        s = (s + (t % 10) * (9 - (m++ % 6))) % 11;
        t = Math.floor(t / 10);
      }

      const expected = s > 0 ? String(s - 1) : 'K';
      return expected === dv;
    },

    /**
     * @param {string} rut
     * @param {{ dots: boolean }} [options]
     */
    format: function (rut, options = { dots: true }) {
      if (!rut) return '';
      rut = this.clean(rut);
      if (!rut || rut.length < 2) return rut;

      if (options.dots) {
        const numberPart = rut.slice(0, -1);
        const checkDigit = rut.slice(-1);
        const groups = [];
        for (let i = numberPart.length; i > 0; i -= 3) {
          const start = Math.max(0, i - 3);
          groups.unshift(numberPart.slice(start, i));
        }
        return groups.join('.') + '-' + checkDigit;
      }

      return rut.slice(0, -1) + '-' + rut.slice(-1);
    },
  };
}

if (!customElements.get('cart-invoice')) {
  customElements.define('cart-invoice', CartInvoice);
}
